import { useEffect } from 'react';
import {
  ArrayField,
  useArrayFieldItemState,
  useArrayFieldItemApi,
} from 'informed';
import { Select, TextField, Checkbox } from '@calefy-inc/informedMaterial';
import RenderedQuestionInstance from '../RenderedQuestionInstance';
import AnimatedRelevant from '../AnimatedRelevant';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ForkRightIcon from '@mui/icons-material/ForkRight';
import { QuestionInstanceTreeDisplay } from '../QuestionInstanceTreeDisplay';
import { logicConditionTrue } from './utility';
import { DisplayFormattedQuestionIdentifier } from '../common';
// types
import type { $TSFixMe } from '@calefy-inc/utilityTypes';
import { QuoteComponentProps, ProgramBuilderComponentProps } from '../types';
import { ProgramBuilderQuestionInstance } from '../../../FormManager/classes';
import { QuoteWizardQuestionInstance } from '../../../QuoteWizard/classes';

import { Component } from '../classes';
import { styled } from '@mui/material';

interface MultiLogicCondition {
  api_name: string;
  operator: string;
  not: boolean;
  condition: string;
}

const verifyIsValidCondition = (
  condition: unknown,
): condition is MultiLogicCondition => {
  return (
    !!condition &&
    typeof condition === 'object' &&
    condition !== null &&
    'api_name' in condition &&
    'operator' in condition &&
    'condition' in condition
  );
};

interface QuoteArrayFieldItemProps {
  sourceQuestion: QuoteWizardQuestionInstance;
  subQuestions: Array<QuoteWizardQuestionInstance>;
  conditions: Array<MultiLogicCondition>;
  classes: $TSFixMe;
  keepStateIfRelevant?: boolean;
}
// Separate component to use hooks inside ArrayField.Items
const QuoteArrayFieldItem = ({
  sourceQuestion,
  subQuestions,
  conditions,
  classes,
  keepStateIfRelevant = false,
}: QuoteArrayFieldItemProps) => {
  // Use the hook to access array field item state
  const { name } = useArrayFieldItemState();
  const arrayFieldItemApi = useArrayFieldItemApi();

  // set the field name for the subquestions
  useEffect(() => {
    if (name) {
      [sourceQuestion, ...subQuestions].forEach((q) => (q.fieldPrefix = name));
    }
  }, [name]);

  if (!sourceQuestion) {
    return <></>;
  }

  //console.log({ sourceValue, matchingCondition, matchingApiName });
  return (
    <>
      <RenderedQuestionInstance
        questionInstance={sourceQuestion}
        key={sourceQuestion.id}
        classes={classes}
        keepStateIfRelevant={keepStateIfRelevant}
      />
      {subQuestions.map((subQuestion) => (
        <AnimatedRelevant
          key={subQuestion.id}
          when={() => {
            // @ts-expect-error
            const sourceValue = arrayFieldItemApi.getValue(
              sourceQuestion.apiName,
            );
            const matchingApiNames = conditions
              .filter((potentialMatchingCondition) => {
                const { not, condition, operator } = potentialMatchingCondition;
                const sourceValueAsString = String(sourceValue);
                const result = logicConditionTrue({
                  sourceValue: sourceValueAsString,
                  operator,
                  not,
                  condition,
                });
                return result;
              })
              .map((matchingCondition) => matchingCondition['api_name']);

            const result = matchingApiNames.includes(subQuestion.apiName);
            return result;
          }}
        >
          <RenderedQuestionInstance
            questionInstance={subQuestion}
            classes={classes}
            keepStateIfRelevant={keepStateIfRelevant}
          />
        </AnimatedRelevant>
      ))}
    </>
  );
};

const QuoteComponent = ({
  questionInstance,
  classes = {},
  keepStateIfRelevant = false,
}: QuoteComponentProps) => {
  let sourceQuestion = questionInstance.subQuestions[0];
  const subQuestions = [...questionInstance.subQuestions].splice(1);
  const { conditions: rawConditions = [] } = questionInstance.propsBlob;
  //console.log({ rawConditions });
  const conditions = rawConditions.filter(verifyIsValidCondition);
  //console.log({ conditions });
  return (
    <>
      <ArrayField name={questionInstance.apiName}>
        {(arrayFieldApi) => {
          if (arrayFieldApi.fields.length === 0) {
            arrayFieldApi.add();
          }
          return (
            <>
              <ArrayField.Items>
                {() => (
                  <QuoteArrayFieldItem
                    sourceQuestion={sourceQuestion}
                    subQuestions={subQuestions}
                    conditions={conditions}
                    classes={classes}
                    keepStateIfRelevant={keepStateIfRelevant}
                  />
                )}
              </ArrayField.Items>
            </>
          );
        }}
      </ArrayField>
    </>
  );
};

interface ProgramBuilderArrayFieldItemProps {
  questionInstance?: ProgramBuilderQuestionInstance;
  classes: $TSFixMe;
  fields: $TSFixMe;
  add: () => void;
}
// Separate component for ProgramBuilder array field items
const ProgramBuilderArrayFieldItem = ({
  questionInstance,
  classes,
  fields,
  add,
}: ProgramBuilderArrayFieldItemProps) => {
  // Use hooks to access array field item state and API
  const { index, values } = useArrayFieldItemState();
  const { remove } = useArrayFieldItemApi();

  const propsBlob =
    questionInstance && questionInstance.propsBlob
      ? questionInstance.propsBlob
      : {};
  const { not, operator, condition } = propsBlob;

  //console.log({ questionInstance });
  return (
    <>
      <ConditionToplineContainer>
        <Typography>
          {index === 0 ? 'and if the answer is' : 'otherwise, if the answer is'}
        </Typography>
        {fields.length > 1 ? (
          <Button onClick={() => remove()}>Remove Condition</Button>
        ) : null}
      </ConditionToplineContainer>
      <PropsBlobConditionContainer>
        <Checkbox
          classes={classes}
          className={classes.checkbox}
          name={`not`}
          label='not'
          value={not}
        />
        <Select
          name={`operator`}
          label='Operator'
          variant='standard'
          defaultValue={operator}
          required={!!questionInstance}
          className={classes.selectField}
          classes={classes}
          fullWidth
          options={[
            { label: 'equal to', value: 'equals' },
            { label: 'greater than', value: 'greater' },
            { label: 'greater than or equal to', value: 'geq' },
            { label: 'less than', value: 'lesser' },
            { label: 'less than or equal to', value: 'leq' },
            { label: 'contains', value: 'contains' },
          ]}
          placeholder='Select the operation you want to perform'
        />
      </PropsBlobConditionContainer>
      <TextField
        name={`condition`}
        value={condition}
        label='Conditional Value'
        placeholder='Conditional goes here'
        required={!!questionInstance}
        classes={classes}
        className={classes.textField}
        variant='standard'
      />

      <Typography>then ask</Typography>
      {questionInstance && questionInstance.subQuestions.length > 0 ? (
        <Select
          name={`api_name`}
          label='Conditional Question'
          className={classes.selectField}
          classes={classes}
          fullWidth
          required={!!questionInstance}
          options={
            questionInstance
              ? questionInstance.subQuestions.slice(1).map((subQ: $TSFixMe) => {
                return {
                  label: subQ.generateDisplayName(),
                  value: subQ.apiName,
                };
              })
              : []
          }
        />
      ) : (
        <Typography>(No subquestions found - add these later!)</Typography>
      )}
      <QuestionInstanceTreeDisplay
        questionInstances={
          questionInstance &&
            questionInstance.subQuestions &&
            questionInstance.subQuestions.length > 1
            ? questionInstance.subQuestions
              .slice(1)
              .filter((q) => q.apiName === values?.[`api_name`])
            : undefined
        }
        fallback='None (add these later)'
      />
      {index === fields.length - 1 ? (
        <Button
          onClick={() => {
            add();
          }}
          startIcon={<AddIcon />}
        >
          Add Condition
        </Button>
      ) : null}
    </>
  );
};

const ProgramBuilderComponent = ({
  classes = {},
  questionInstance,
}: /* parentForm, */
  ProgramBuilderComponentProps) => {
  /* // @ts-expect-error */
  /* const question_instances = parentForm.questionInstances || []; */

  //Source component select drop down
  //"OnChange" we might want to define what the possible results are based on the component type (Yes/No)
  return (
    <ArrayField name='props_blob_conditions'>
      {(arrayFieldArgs) => {
        //console.log({ arrayFieldArgs });
        const { fields, add } = arrayFieldArgs;
        if (fields.length === 0) {
          add();
        }
        return (
          <>
            <Typography>
              First ask{' '}
              <DisplayFormattedQuestionIdentifier
                questionInstance={questionInstance?.subQuestions?.[0]}
                fallback='None (add this later)'
              />
              {' ,'}
            </Typography>
            <ArrayField.Items>
              {() => (
                <ProgramBuilderArrayFieldItem
                  questionInstance={questionInstance}
                  classes={classes}
                  fields={fields}
                  add={add}
                />
              )}
            </ArrayField.Items>
          </>
        );
      }}
    </ArrayField>
  );
};

const ConditionToplineContainer = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
});
const PropsBlobConditionContainer = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(2),
  };
});

const validate = (questionInstance: ProgramBuilderQuestionInstance) => {
  const propsBlob = questionInstance.propsBlob;
  if (!('conditions' in propsBlob)) {
    return [
      `No conditions were found for multibranched logic question with API name ${questionInstance.apiName}`,
    ];
  }
  const { conditions } = propsBlob;
  if (!Array.isArray(conditions)) {
    return [
      `Conditions for multibranched logic question with API name ${questionInstance.apiName} is malformed; please delete it and recreate it`,
    ];
  }
  if (conditions.length === 0) {
    return [
      `No conditions found for multibranched logic question with API name ${questionInstance.apiName} (length 0)`,
    ];
  }
  const invalidConditions = conditions.filter(
    (condition) => !verifyIsValidCondition(condition),
  );
  if (invalidConditions.length > 0) {
    return invalidConditions.map(
      (condition) =>
        `Condition testing condition ${condition.condition} resulting in answers with API name '${condition['api_name']}' is invalid; please check that all fields are filled in`,
    );
  }
  //console.log({ conditions });
  const missingApiNamesConditions = conditions.filter((condition) => {
    const testApiName = condition['api_name'];
    const matched = questionInstance.subQuestions.find(
      (q) => q.apiName === testApiName,
    );
    //console.log({ testApiName, matched });
    return !matched;
  });
  //console.log({ missingApiNamesConditions });
  if (missingApiNamesConditions.length > 0) {
    return missingApiNamesConditions.map(
      (condition) =>
        `Condition with test ${condition.condition} should display the quesstion with API name '${condition['api_name']}'. However, no subquestion with that API name exists.`,
    );
  }
  return [];
};

export const createMultibranchedLogicComponentRegistry = () => {
  return new Component({
    type: 'multibranchedLogic',
    typeLabel: 'Conditional Question (Multi Branch)',
    quoteComponent: QuoteComponent,
    programBuilderComponent: ProgramBuilderComponent,
    programBuilderValidation: validate,
    dataType: 'nested',
    icon: () => <ForkRightIcon />,
  });
};
